import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search } from "lucide-react";

function toValueList(value) {
  if (Array.isArray(value)) return value.map(String).filter((item) => item !== "");
  if (value == null || value === "") return [];
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function emitValues(value, next) {
  return Array.isArray(value) ? next : next.join(",");
}

function triggerLabel(options, selectedValues, placeholder) {
  if (!selectedValues.length) return placeholder;
  const labels = selectedValues
    .map((value) => options.find((item) => String(item.value) === String(value))?.label)
    .filter(Boolean);
  if (!labels.length) return placeholder;
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]}, ${labels[1]}`;
  return `${labels[0]} +${labels.length - 1}`;
}

function FilterSelect({
  value,
  onChange,
  options = [],
  placeholder = "Select",
  loading = false,
  searchable = false,
  searchPlaceholder = "Search...",
  multiple = false,
  menuClassName = "",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const selectedValues = toValueList(value);
  const hasSelection = selectedValues.length > 0;
  const label = loading ? "Loading..." : triggerLabel(options, selectedValues, placeholder);

  const visibleOptions = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return options;
    return options.filter((item) => {
      if (item.value === "") return false;
      return String(item.label || "").toLowerCase().includes(term);
    });
  }, [options, query]);

  const updatePos = () => {
    const box = triggerRef.current?.getBoundingClientRect();
    if (!box) return;
    setPos({ top: box.bottom + 8, left: box.left, width: box.width });
  };

  useEffect(() => {
    if (!open) {
      setQuery("");
      return undefined;
    }
    updatePos();
    const onDoc = (event) => {
      if (triggerRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);
    return () => {
      window.removeEventListener("mousedown", onDoc);
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
    };
  }, [open]);

  const selectOption = (itemValue) => {
    const nextValue = String(itemValue ?? "");
    if (!multiple) {
      onChange(itemValue);
      setOpen(false);
      return;
    }
    if (nextValue === "") {
      onChange(emitValues(value, []));
      return;
    }
    const exists = selectedValues.includes(nextValue);
    const next = exists
      ? selectedValues.filter((item) => item !== nextValue)
      : [...selectedValues, nextValue];
    onChange(emitValues(value, next));
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`bw-select-trigger${open ? " is-open" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        disabled={loading}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={hasSelection ? "" : "is-placeholder"}>{label}</span>
        <ChevronDown size={16} />
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              className={`bw-select-menu${menuClassName ? ` ${menuClassName}` : ""}`}
              style={{ top: pos.top, left: pos.left, width: pos.width }}
              role="listbox"
              aria-multiselectable={multiple || undefined}
            >
              {searchable ? (
                <label className="bw-select-search">
                  <Search size={14} />
                  <input
                    autoFocus
                    placeholder={searchPlaceholder}
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                  />
                </label>
              ) : null}
              {visibleOptions.length ? (
                visibleOptions.map((item) => {
                  const itemValue = String(item.value ?? "");
                  const active = itemValue === "" ? !hasSelection : selectedValues.includes(itemValue);
                  return (
                    <button
                      key={itemValue || "all"}
                      type="button"
                      className={`bw-select-option${multiple ? " is-multi" : ""}${active ? " is-active" : ""}`}
                      onClick={() => selectOption(item.value)}
                      role="option"
                      aria-selected={active}
                    >
                      {multiple ? (
                        <span className={`bw-select-check${active ? " is-on" : ""}`}>
                          {active ? <Check size={11} /> : null}
                        </span>
                      ) : null}
                      <span>{item.label}</span>
                      {!multiple && active ? <Check size={16} /> : null}
                    </button>
                  );
                })
              ) : (
                <p className="bw-select-empty">No matches</p>
              )}
            </div>,
            document.body
          )
        : null}
    </>
  );
}

export default FilterSelect;
